import { useQuery } from "@tanstack/react-query";
import { Flame, Film, Users, MessageCircleHeart, Sparkles } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { getTopViralClips, getTopCreators } from "@/lib/worldPulseLive.functions";
import { AnimatedNumber } from "./PulseDeltaBadge";
import {
  MOCK_VIRAL_CLIPS,
  MOCK_NATION_SCORES,
  MOCK_PREDICTION_EVENTS,
} from "@/lib/demo/executiveDemoMock";

/* WORLD-CUP-LAUNCH-P2 — foundational fallback content (editorial, no fake users). */
const FOUNDATION_CLIPS = MOCK_VIRAL_CLIPS.slice(0, 6).map((c) => ({
  id: c.id,
  title: c.title,
  subtitle: `${c.nation_code} · ${c.platform}`,
  metric: c.shares,
  metricLabel: "shares",
}));

const FOUNDATION_MOMENTS = MOCK_VIRAL_CLIPS.slice(6, 12).map((c) => ({
  id: c.id,
  title: c.title,
  subtitle: `${c.nation_code} · editorial`,
  metric: c.views,
  metricLabel: "views",
}));

const FOUNDATION_RIVALRIES = [
  { id: "r-arg-bra", title: "🇦🇷 vs 🇧🇷", subtitle: "Clásico sudamericano",     metric: 92, metricLabel: "heat" },
  { id: "r-mex-usa", title: "🇲🇽 vs 🇺🇸", subtitle: "Rivalidad CONCACAF",       metric: 87, metricLabel: "heat" },
  { id: "r-esp-fra", title: "🇪🇸 vs 🇫🇷", subtitle: "Tiki-taka vs Les Bleus",   metric: 81, metricLabel: "heat" },
  { id: "r-col-per", title: "🇨🇴 vs 🇵🇪", subtitle: "Rivalidad andina",         metric: 74, metricLabel: "heat" },
  { id: "r-bra-deu", title: "🇧🇷 vs 🇩🇪", subtitle: "Revancha del 7-1",         metric: 78, metricLabel: "heat" },
];

const FOUNDATION_PREDICTIONS = MOCK_PREDICTION_EVENTS.slice(0, 5).map((p, i) => ({
  id: p.id,
  title: p.question,
  subtitle: `${p.option_a} vs ${p.option_b}`,
  metric: p.points_reward * 10 + (5 - i) * 30,
  metricLabel: "votos",
}));

/**
 * "Lo que está explotando ahora" — Netflix-style horizontal carousel of
 * top viral activity: clips, creators, reactions, predictions, moments.
 * Only renders entries backed by real rows. Empty groups show a discreet
 * "Esperando señales" tile (no mock data).
 */
export function TrendingNow() {
  const clipsQ = useQuery({
    queryKey: ["worldPulse", "top_clips"],
    queryFn: () => getTopViralClips(),
    refetchInterval: 20_000,
  });
  const creatorsQ = useQuery({
    queryKey: ["worldPulse", "top_creators"],
    queryFn: () => getTopCreators(),
    refetchInterval: 30_000,
  });

  const clips = clipsQ.data?.rows ?? [];
  const creators = creatorsQ.data?.rows ?? [];

  return (
    <section
      aria-label="Lo que está explotando ahora"
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[oklch(0.10_0.03_280)] to-[oklch(0.07_0.02_290)] p-4 shadow-[0_20px_60px_-20px_rgba(0,0,0,.6)]"
    >
      <header className="mb-3 flex items-center gap-2">
        <Flame className="h-4 w-4 text-orange-400" />
        <h2 className="text-xs font-black uppercase tracking-[0.18em] text-white/90">
          Momentos que podrían explotar
        </h2>
      </header>

      <div className="space-y-4">
        <Lane title="Goles históricos · listos para revivir" icon={<Film className="h-3 w-3" />}>
          {clips.length === 0 ? (
            FOUNDATION_CLIPS.map((c) => (
              <Card key={c.id} to="/media-blitz" title={c.title} subtitle={c.subtitle} metric={c.metric} metricLabel={c.metricLabel} editorial />
            ))
          ) : (
            clips.map((c) => (
              <Card
                key={c.id}
                to="/viral-cards"
                title={`${c.home_team ?? ""} ${c.score ? `· ${c.score}` : ""} ${c.away_team ? `vs ${c.away_team}` : ""}`.trim() || "Clip viral"}
                subtitle={c.emotion ? `${c.emotion.toUpperCase()} · ${c.minute ?? ""}` : c.platform ?? "VerseSport"}
                metric={c.shares ?? 0}
                metricLabel="shares"
              />
            ))
          )}
        </Lane>

        <Lane title="Rivalidades en construcción" icon={<Users className="h-3 w-3" />}>
          {creators.length === 0 ? (
            FOUNDATION_RIVALRIES.map((r) => (
              <Card key={r.id} to="/nation-wars" title={r.title} subtitle={r.subtitle} metric={r.metric} metricLabel={r.metricLabel} editorial />
            ))
          ) : (
            creators.map((c) => (
              <Card
                key={c.user_id}
                to="/nation-wars"
                title={c.display_name ?? "Fan"}
                subtitle={`${c.clips} clips · ${c.views} views`}
                metric={c.score}
                metricLabel="score"
                avatar={c.avatar_url ?? undefined}
              />
            ))
          )}
        </Lane>

        <Lane title="Predicciones destacadas" icon={<MessageCircleHeart className="h-3 w-3" />}>
          {clips.filter((c) => (c.clicks ?? 0) > 0).slice(0, 5).length === 0 ? (
            FOUNDATION_PREDICTIONS.map((p) => (
              <Card key={p.id} to="/quinielas" title={p.title} subtitle={p.subtitle} metric={p.metric} metricLabel={p.metricLabel} editorial />
            ))
          ) : (
            clips
              .slice()
              .sort((a, b) => (b.clicks ?? 0) - (a.clicks ?? 0))
              .slice(0, 5)
              .map((c) => (
                <Card
                  key={"r-" + c.id}
                  to="/quinielas"
                  title={c.emotion ? c.emotion.toUpperCase() : "Reacción"}
                  subtitle={`${c.home_team ?? ""} ${c.away_team ? "vs " + c.away_team : ""}`}
                  metric={c.clicks ?? 0}
                  metricLabel="clicks"
                />
              ))
          )}
        </Lane>

        <Lane title="Momentos cinematográficos" icon={<Sparkles className="h-3 w-3" />}>
          {clips.slice(0, 5).length === 0 ? (
            FOUNDATION_MOMENTS.map((m) => (
              <Card key={m.id} to="/showcase" title={m.title} subtitle={m.subtitle} metric={m.metric} metricLabel={m.metricLabel} editorial />
            ))
          ) : (
            clips
              .slice()
              .sort((a, b) => (b.views ?? 0) - (a.views ?? 0))
              .slice(0, 5)
              .map((c) => (
                <Card
                  key={"m-" + c.id}
                  to="/viral-cards"
                  title={`Min ${c.minute ?? "—"}`}
                  subtitle={`${c.home_team ?? ""} ${c.score ?? ""} ${c.away_team ?? ""}`.trim()}
                  metric={c.views ?? 0}
                  metricLabel="views"
                />
              ))
          )}
        </Lane>
      </div>
    </section>
  );
}

function Lane({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-white/60">
        {icon}
        <span className="text-[10px] font-bold uppercase tracking-wider">{title}</span>
      </div>
      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {children}
      </div>
    </div>
  );
}

function Card({
  title, subtitle, metric, metricLabel, avatar, to, editorial,
}: { title: string; subtitle?: string; metric: number; metricLabel: string; avatar?: string; to?: string; editorial?: boolean }) {
  const inner = (
    <>
      <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br from-primary/15 via-transparent to-fuchsia-500/10 opacity-60" />
      {editorial && (
        <div className="absolute right-2 top-2 rounded-full bg-white/10 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest text-white/70">
          editorial
        </div>
      )}
      {avatar ? (
        <img src={avatar} alt="" className="mb-1.5 h-7 w-7 rounded-full border border-white/20 object-cover" />
      ) : null}
      <div className="line-clamp-1 text-xs font-black text-white">{title}</div>
      {subtitle && <div className="mt-0.5 line-clamp-1 text-[10px] text-white/50">{subtitle}</div>}
      <div className="mt-2 flex items-baseline gap-1">
        <AnimatedNumber value={metric} className="text-lg font-black tabular-nums text-white" />
        <span className="text-[9px] uppercase tracking-wider text-white/40">{metricLabel}</span>
      </div>
    </>
  );
  const className =
    "relative min-w-[170px] shrink-0 overflow-hidden rounded-xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:border-white/20 cursor-pointer block";
  if (to) {
    return (
      <Link to={to} className={className}>
        {inner}
      </Link>
    );
  }
  return <div className={className}>{inner}</div>;
}